import { useApolloClient, useQuery } from '@apollo/react-hooks';
import { Header } from '@mds/ui';
import Loader from 'components/ui/LoadingIndicator';
import {
    Box,
    Breadcrumbs,
    Button,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    Link,
    Radio,
    RadioGroup,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { MessageView } from 'components/ui/Feedback';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useHistory, useParams } from 'react-router-dom';
import { GET_PREMIUM_CHANNEL_BY_ID_LEAN } from '../../../gql/channels';
import { MY_BILLING_ACCOUNTS } from '../../../gql/users';
import { Channel, ChannelSubscriptionType } from '../../../shared/types';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { getChannelSubscriptionLink } from '../../../utils/link.utils';
import { useLogger } from '../../providers/logger/Logger';
import { LogAction, LogLevel } from '../../providers/logger/logger-types';
import { FurtherAssistanceEmail } from './FurtherAssistanceEmail';
import { PremiumChannelAccountPayment } from './PremiumChannelAccountPayment';
import { PremiumChannelInvoicePayment } from './PremiumChannelInvoicePayment';
import { StripePaymentStepper } from './StripePaymentStepper';
import { isValidMongoId } from '../../../utils/id-validation.utils';

interface Account {
    id: string;
    name: string;
}

export interface SelectOption {
    label: string;
    value: string;
}

const PremiumChannelPaymentScreen = () => {
    const history = useHistory();
    const { channelId } = useParams<{ channelId: string }>();
    const isValidChannelId = isValidMongoId(channelId);

    const client = useApolloClient();
    const { printLog } = useLogger();
    const [paymentType, setPaymentType] = useState<ChannelSubscriptionType>();
    const [billingAccounts, setBillingAccounts] = useState<SelectOption[]>([]);
    const [isPaymentFormShown, setPaymentFormShown] = useState<boolean>(false);
    const [isPaymentTypeChosen, setPaymentTypeChosen] =
        useState<boolean>(false);
    const successUrl = `${baseUrls.browse.channelMain}/${channelId}/subscribed`;
    const payingInvoiceSuccessUrl = `${baseUrls.browse.channelMain}/${channelId}/paying-invoice-requested`;

    const { data } = useQuery<{ channel: Channel }>(
        GET_PREMIUM_CHANNEL_BY_ID_LEAN,
        {
            skip: !isValidChannelId,
            variables: { id: channelId },
            fetchPolicy: 'cache-first',
        }
    );

    const { data: me, loading: billingAccountsLoading } = useQuery<{
        myBillingAccounts: Account[];
    }>(MY_BILLING_ACCOUNTS, {
        skip: !isValidChannelId,
    });
    const accountsToSelectOption = (acc: Account[]): SelectOption[] =>
        acc.map((a) => ({ label: a.id, value: a.id }));

    const fetchBillingAccounts = (
        filter: string,
        callback: (options: SelectOption[]) => void
    ) => {
        client
            .query<{ myBillingAccounts: Account[] }>({
                query: MY_BILLING_ACCOUNTS,
                fetchPolicy: 'cache-first',
                variables: {
                    filter: filter.trim().toUpperCase(),
                },
            })
            .then((response) => {
                const accounts = response.data.myBillingAccounts;
                const selectOptions = accountsToSelectOption(accounts);
                callback(selectOptions);
            })
            .catch((error) => {
                printLog(
                    LogLevel.ERROR,
                    LogAction.GetAccounts,
                    `Unable to get accounts using filter: ${filter ?? '<undefined>'}`,
                    error
                );
            });
    };

    useEffect(() => {
        if (!isValidChannelId) return;
        if (!data) return;

        if (
            data.channel.userChannelSubscription.subscribed &&
            data.channel.userChannelSubscription.subscriptionType !==
                ChannelSubscriptionType.Trial
        ) {
            history.replace(`${baseUrls.browse.channelMain}/${channelId}/`);
        }
    }, [data, channelId, history, isValidChannelId]);

    useEffect(() => {
        if (!isValidChannelId) return;
        if (!me) return;
        setBillingAccounts(accountsToSelectOption(me.myBillingAccounts));
    }, [isValidChannelId, me]);

    useEffect(() => {
        if (paymentType || isPaymentFormShown) setPaymentTypeChosen(false);
    }, [paymentType, isPaymentFormShown]);

    if (!isValidChannelId) {
        return (
            <MessageView
                styleName="primary"
                size="large"
                title="Channel not found"
                primaryAction={{
                    label: 'Return to Neon Insights',
                    onClick: () => history.push('/'),
                }}
            />
        );
    }

    if (!data || billingAccountsLoading) {
        return <Loader />;
    }

    const channel = data.channel;
    const myBillingAccounts = me?.myBillingAccounts || [];
    const noBillingAccounts = myBillingAccounts.length < 1;
    const noBillingAccountsMessage =
        'To bill to a clearing account you need to be onboarded as a Marex client. Contact the Neon sales team at neonsales@marex.com to find out more.';

    return (
        <Grid container spacing={4}>
            <Grid size={12}>
                <Header
                    title="Confirm subscription"
                    description="Choose how you want to pay your monthly subscription."
                    breadcrumbs={
                        <Breadcrumbs aria-label="Subscription breadcrumb">
                            <Link
                                component={RouterLink}
                                to={baseUrls.channels.main}
                            >
                                All channels
                            </Link>
                            <Link
                                component={RouterLink}
                                to={`${baseUrls.browse.channelMain}/${data.channel._id}`}
                            >
                                {data.channel.name}
                            </Link>
                            <Link
                                component={RouterLink}
                                to={getChannelSubscriptionLink(channelId)}
                            >
                                Subscribe
                            </Link>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'text.primary',
                                }}
                            >
                                Confirm
                            </Typography>
                        </Breadcrumbs>
                    }
                />
            </Grid>
            <Grid size={12}>
                <Grid
                    container
                    spacing={4}
                    sx={{
                        justifyContent: 'space-between',
                    }}
                >
                    <Grid
                        size={{
                            xs: 12,
                            md: 8,
                            lg: 6,
                        }}
                    >
                        <Stack spacing={4}>
                            <Stack spacing={1}>
                                <Typography variant="body1">
                                    Get access to Premium content
                                </Typography>
                                <Typography variant="h5">{`$${channel.price}/month*`}</Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.secondary',
                                    }}
                                >
                                    *Price excludes applicable sales taxes
                                </Typography>
                            </Stack>

                            {!isPaymentFormShown && (
                                <>
                                    <FormControl
                                        error={
                                            isPaymentTypeChosen && !paymentType
                                        }
                                    >
                                        <RadioGroup
                                            value={paymentType ?? ''}
                                            onChange={(_, value) =>
                                                setPaymentType(
                                                    value as ChannelSubscriptionType
                                                )
                                            }
                                        >
                                            <Stack spacing={2}>
                                                <FormControlLabel
                                                    value={
                                                        ChannelSubscriptionType.Stripe
                                                    }
                                                    control={
                                                        <Radio id="credit-card-radio-button" />
                                                    }
                                                    label="Pay with credit or debit card"
                                                />
                                                <Tooltip
                                                    title={
                                                        noBillingAccounts
                                                            ? noBillingAccountsMessage
                                                            : ''
                                                    }
                                                >
                                                    <Box>
                                                        <FormControlLabel
                                                            value={
                                                                ChannelSubscriptionType.Account
                                                            }
                                                            control={
                                                                <Radio id="marex-account-radio-button" />
                                                            }
                                                            label="Bill to my Marex Clearing account"
                                                            disabled={
                                                                noBillingAccounts
                                                            }
                                                        />
                                                    </Box>
                                                </Tooltip>
                                                <FormControlLabel
                                                    value={
                                                        ChannelSubscriptionType.External
                                                    }
                                                    control={
                                                        <Radio id="invoice-radio-button" />
                                                    }
                                                    label="Pay with invoice"
                                                />
                                            </Stack>
                                        </RadioGroup>
                                        {isPaymentTypeChosen && !paymentType ? (
                                            <FormHelperText>
                                                Payment type should be chosen
                                            </FormHelperText>
                                        ) : null}
                                    </FormControl>
                                    <Box>
                                        <Button
                                            variant="contained"
                                            onClick={() => {
                                                setPaymentTypeChosen(true);
                                                paymentType &&
                                                    setPaymentFormShown(true);
                                            }}
                                        >
                                            Continue
                                        </Button>
                                    </Box>
                                </>
                            )}

                            {isPaymentFormShown &&
                            paymentType === ChannelSubscriptionType.Account ? (
                                <PremiumChannelAccountPayment
                                    channelId={channelId}
                                    channelName={data.channel.name}
                                    fetchBillingAccounts={fetchBillingAccounts}
                                    successUrl={successUrl}
                                    billingAccounts={billingAccounts}
                                    goBack={() => setPaymentFormShown(false)}
                                />
                            ) : null}
                            {isPaymentFormShown &&
                            paymentType === ChannelSubscriptionType.Stripe ? (
                                <StripePaymentStepper
                                    channelId={channelId}
                                    successUrl={successUrl}
                                    goBack={() => setPaymentFormShown(false)}
                                />
                            ) : null}
                            {isPaymentFormShown &&
                            paymentType === ChannelSubscriptionType.External ? (
                                <PremiumChannelInvoicePayment
                                    channelId={channelId}
                                    successUrl={payingInvoiceSuccessUrl}
                                    goBack={() => setPaymentFormShown(false)}
                                />
                            ) : null}
                        </Stack>
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            md: 4,
                            lg: 4,
                        }}
                    >
                        <FurtherAssistanceEmail />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default PremiumChannelPaymentScreen;
